import React, { useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAdminEvent, useApp } from "@/context/AppContext";
import { DemoUserSwitcher } from "@/components/DemoUserSwitcher";
import CreateEventModal from "@/admin/CreateEventModal";
import {
  LayoutDashboard, Users, Tags, CalendarDays, Plane, BedDouble, Car, Megaphone,
  BookOpen, UploadCloud, Wallet, Settings as SettingsIcon, ExternalLink, Home,
  CalendarRange, ChevronDown, Check, Grid3x3, ChevronRight, LogOut, Database, Smartphone, Trophy, FileSpreadsheet, Compass, Brain, Cake,
} from "lucide-react";

const EVENT_NAV = [
  { to: "/manager/overview", label: "Overview", icon: LayoutDashboard, alias: ["/manager"] },
  { to: "/manager/attendees", label: "Attendees", icon: Users },
  { to: "/manager/groups", label: "Groups", icon: Tags },
  { to: "/manager/schedule", label: "Schedule", icon: CalendarDays },
  { to: "/manager/travel", label: "Travel", icon: Plane },
  { to: "/manager/gamification", label: "Gamification", icon: Trophy },
  { to: "/manager/quest", label: "PC Quest", icon: Brain },
  { to: "/manager/birthdays", label: "Birthdays", icon: Cake },
  { to: "/manager/hotel-rooming", label: "Hotel / Rooming", icon: BedDouble },
  { to: "/manager/transfers", label: "Transfers", icon: Car },
  { to: "/manager/announcements", label: "Announcements", icon: Megaphone },
  { to: "/manager/know-before", label: "Know Before You Go", icon: BookOpen },
  { to: "/manager/explore", label: "Explore", icon: Compass },
  { to: "/manager/data", label: "Data & Imports", icon: Database },
  { to: "/manager/pc-master-data", label: "PC Master Data", icon: FileSpreadsheet },
  { to: "/manager/budget", label: "Budget", icon: Wallet },
  { to: "/manager/settings", label: "Settings", icon: SettingsIcon },
];

// Events where the legacy Gamification sidebar entry is hidden because PC Quest
// is the sole competition interface. The /manager/gamification ROUTE still works
// (reachable via PC Quest -> Technical details -> Legacy gamification history),
// so historical records, audit logs and CSV exports stay accessible.
const LEGACY_GAM_NAV_HIDDEN = new Set([
  "presidents-club-2026",
  "pc-quest-preview",
  "pc-quest-rehearsal",
]);

const STATUS_CLS = {
  upcoming: "bg-[hsl(var(--mint))] text-[hsl(var(--primary))]",
  live: "bg-[hsl(var(--mint))] text-[hsl(var(--primary))]",
  planning: "bg-[hsl(var(--secondary)/0.15)] text-[hsl(var(--secondary))]",
  draft: "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]",
  past: "bg-[hsl(var(--pc-gold-2)/0.25)] text-[hsl(38_40%_32%)]",
  completed: "bg-[hsl(var(--pc-gold-2)/0.25)] text-[hsl(38_40%_32%)]",
  archived: "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]",
};

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { event, events, setAdminEvent } = useAdminEvent();
  const { demoMode, signOut, dualAccess } = useApp();
  const [picker, setPicker] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const readOnly = event && ["past", "completed", "archived"].includes(event.status);
  // Hide the legacy Gamification entry for PC Quest events; keep every other
  // entry and preserve behaviour for all other events.
  const visibleNav = EVENT_NAV.filter(
    (n) => !(n.to === "/manager/gamification" && LEGACY_GAM_NAV_HIDDEN.has(event?.id)),
  );
  const current = EVENT_NAV.find((n) => (n.alias || []).includes(location.pathname) || location.pathname === n.to || (n.to !== "/manager/overview" && location.pathname.startsWith(n.to)));
  const title = location.pathname === "/manager/events" ? "All Events" : (current?.label || "Overview");

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      {/* Sidebar */}
      <aside data-testid="admin-sidebar-nav" className="fixed inset-y-0 left-0 z-40 hidden lg:flex w-[var(--admin-sidebar-w)] flex-col bg-[hsl(var(--primary))] text-white/90">
        <div className="px-5 py-4 border-b border-white/10">
          <img src="/brand/collabera-logo-white.png" alt="Collabera" className="h-6" />
          <div className="mt-1.5 text-[10px] uppercase tracking-[0.22em] text-white/50 font-semibold">Events Manager</div>
        </div>

        {/* Global */}
        <div className="px-3 pt-3">
          <div className="px-2 text-[10px] uppercase tracking-widest text-white/40 font-semibold mb-1">Collabera Events</div>
          <button data-testid="admin-view-all-events" onClick={() => navigate("/manager/events")} className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium ${location.pathname === "/manager/events" ? "bg-white/10 text-white" : "text-white/70 hover:bg-white/5 hover:text-white"}`}>
            <Grid3x3 className="h-4.5 w-4.5" /> Events
          </button>
        </div>

        {/* Current event selector */}
        <div className="px-3 pt-3">
          <div className="px-2 text-[10px] uppercase tracking-widest text-white/40 font-semibold mb-1">Current Event</div>
          <div className="relative">
            <button data-testid="admin-event-selector" onClick={() => setPicker((v) => !v)} className="flex w-full items-center justify-between gap-2 rounded-xl bg-white/10 px-3 py-2.5 text-left">
              <span className="text-sm font-semibold text-white truncate">{event?.name || "Select event"}</span>
              <ChevronDown className="h-4 w-4 text-white/60 shrink-0" />
            </button>
            {picker && (
              <div data-testid="admin-event-selector-menu" className="absolute z-50 mt-1 w-full rounded-xl bg-white shadow-xl border border-[hsl(var(--border))] overflow-hidden">
                {events.map((e) => (
                  <button key={e.id} data-testid="admin-event-option" onClick={() => { setAdminEvent(e.id); setPicker(false); navigate("/manager/overview"); }} className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left text-sm hover:bg-[hsl(var(--muted)/0.6)]">
                    <span className="text-[hsl(var(--primary))] font-medium truncate">{e.name}</span>
                    {e.id === event?.id && <Check className="h-4 w-4 text-[hsl(var(--secondary))]" />}
                  </button>
                ))}
                <button onClick={() => { setPicker(false); navigate("/manager/events"); }} className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm font-semibold text-[hsl(var(--secondary))] border-t border-[hsl(var(--border))]">
                  <Grid3x3 className="h-4 w-4" /> View All Events
                </button>
              </div>
            )}
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto no-scrollbar px-3 py-3 space-y-0.5">
          {visibleNav.map((n) => {
            const Icon = n.icon;
            const active = location.pathname === n.to || (n.alias || []).includes(location.pathname);
            return (
              <NavLink key={n.to} to={n.to} data-testid="admin-sidebar-nav-item"
                className={`flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition-colors ${active ? "bg-white/10 text-white border-l-2 border-[hsl(var(--pc-gold))]" : "text-white/65 hover:bg-white/5 hover:text-white border-l-2 border-transparent"}`}>
                <Icon className="h-4.5 w-4.5" /> {n.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="p-3 border-t border-white/10">
          {dualAccess && (
            <button data-testid="admin-return-to-attendee" onClick={() => navigate("/app")} className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-white/65 hover:bg-white/5 hover:text-white">
              <Smartphone className="h-4.5 w-4.5" /> Return to Attendee App
            </button>
          )}
          {demoMode ? (
            <button data-testid="admin-platform-home" onClick={() => navigate("/")} className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-white/65 hover:bg-white/5 hover:text-white">
              <Home className="h-4.5 w-4.5" /> Platform home
            </button>
          ) : (
            <button data-testid="admin-signout-button" onClick={signOut} className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-white/65 hover:bg-white/5 hover:text-white">
              <LogOut className="h-4.5 w-4.5" /> Sign out
            </button>
          )}
        </div>
      </aside>

      {/* Main */}
      <div className="lg:pl-[var(--admin-sidebar-w)]">
        {/* Workspace header */}
        <header data-testid="admin-workspace-header" className="sticky top-0 z-30 border-b border-[hsl(var(--border))] bg-[hsl(var(--background))]/95 backdrop-blur px-4 lg:px-8 py-3">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-xs text-[hsl(var(--muted-foreground))]">
                <button onClick={() => navigate("/manager/events")} className="hover:text-[hsl(var(--secondary))]">Events</button>
                <ChevronRight className="h-3 w-3" />
                <span className="truncate text-[hsl(var(--primary))] font-medium">{event?.name}</span>
                <ChevronRight className="h-3 w-3" />
                <span>{title}</span>
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                <h1 className="font-serif text-2xl text-[hsl(var(--primary))]">{event?.name}</h1>
                {event?.status && <span className={`text-[11px] rounded-full px-2 py-0.5 font-bold capitalize ${STATUS_CLS[event.status] || STATUS_CLS.draft}`}>{event.status}</span>}
                {readOnly && <span className="text-[11px] rounded-full px-2 py-0.5 font-semibold bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]">Read only · Archived</span>}
              </div>
              {event?.destination && event.destination !== "TBD" && (
                <div className="text-xs text-[hsl(var(--muted-foreground))]">{event.destination} · {event.date_label}{event.host_resort && event.host_resort !== "TBD" ? ` · ${event.host_resort}` : ""}</div>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button data-testid="admin-open-attendee-experience" onClick={() => navigate("/app")} className="hidden sm:inline-flex items-center gap-2 rounded-full bg-[hsl(var(--primary))] text-white px-4 py-2 text-sm font-semibold hover:opacity-90">
                Attendee App <ExternalLink className="h-4 w-4" />
              </button>
              {demoMode && <DemoUserSwitcher variant="compact" />}
            </div>
          </div>
        </header>

        {/* Mobile event + nav */}
        <div className="lg:hidden border-b border-[hsl(var(--border))] bg-white">
          <div className="px-3 py-2 flex items-center gap-2">
            <select data-testid="admin-event-selector-mobile" value={event?.id || ""} onChange={(e) => { setAdminEvent(e.target.value); navigate("/manager/overview"); }} className="rounded-lg border border-[hsl(var(--border))] px-2 py-1.5 text-sm font-semibold text-[hsl(var(--primary))]">
              {events.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
            <button onClick={() => navigate("/manager/events")} className="text-xs font-semibold text-[hsl(var(--secondary))]">All events</button>
          </div>
          <div className="overflow-x-auto no-scrollbar"><div className="flex gap-1 px-3 pb-2">
            {visibleNav.map((n) => (
              <NavLink key={n.to} to={n.to} className={({ isActive }) => `whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold ${isActive ? "bg-[hsl(var(--primary))] text-white" : "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]"}`}>{n.label}</NavLink>
            ))}
          </div></div>
        </div>

        <main className="px-4 lg:px-8 py-6 max-w-6xl">
          {event?.is_preview_test_event && (
            <div data-testid="admin-preview-test-banner" className="mb-4 rounded-lg bg-[hsl(var(--secondary))] text-white text-center text-xs font-bold uppercase tracking-[0.18em] py-2">
              Preview Test Event — synthetic data, safe to edit
            </div>
          )}
          <Outlet context={{ openCreate: () => setCreateOpen(true) }} />
        </main>
      </div>

      <CreateEventModal open={createOpen} onClose={() => setCreateOpen(false)} onCreated={() => navigate("/manager/overview")} />
    </div>
  );
}
